/**
 * NovaAuditoriaPage — upload dos documentos e disparo da leitura (F10).
 *
 * A auditoria tem DUAS fases: esta tela dispara a LEITURA (classifica o que
 * há em cada arquivo); a análise em si só começa depois que o usuário
 * confirma quais documentos entram, em SelecaoDocumentosPage.
 *
 * A FASE do processo muda o que se audita: na preparatória, as peças que
 * instruem a contratação (arts. 18 e 25 da Lei 14.133/2021); na de
 * julgamento, propostas, habilitação e recursos (arts. 59 a 71).
 */

import {
  faCloudUploadAlt,
  faExclamationTriangle,
  faFileAlt,
  faLandmark,
  faSpinner,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  configuracaoAuditoria,
  criarAuditoria,
  decretosDoMunicipio,
} from '../../api/auditoria'
import { useToast } from '../../components/Toast'

const EXTENSOES = '.pdf,.doc,.docx,.txt'

export function NovaAuditoriaPage() {
  const navegar = useNavigate()
  const { notificar } = useToast()
  const entrada = useRef<HTMLInputElement>(null)

  const { data: config, isLoading } = useQuery({
    queryKey: ['auditoria', 'configuracao'],
    queryFn: configuracaoAuditoria,
  })

  const [fase, setFase] = useState('preparatoria')
  const [arquivos, setArquivos] = useState<File[]>([])
  const [tipos, setTipos] = useState<string[]>([])
  const [municipio, setMunicipio] = useState('')
  const [decretos, setDecretos] = useState<number[]>([])
  const [decretosNovos, setDecretosNovos] = useState<File[]>([])
  const [erro, setErro] = useState('')

  const tiposDaFase = useMemo(
    () =>
      fase === 'julgamento'
        ? (config?.tipos.julgamento ?? [])
        : (config?.tipos.preparatoria ?? []),
    [config, fase],
  )

  const { data: decretosDisponiveis } = useQuery({
    queryKey: ['auditoria', 'decretos', municipio],
    queryFn: () => decretosDoMunicipio(municipio),
    enabled: municipio.length > 0,
  })

  const criar = useMutation({
    mutationFn: () =>
      criarAuditoria({
        arquivos,
        tipos,
        fase,
        municipio,
        decretos,
        decretosNovos,
      }),
    onSuccess: (resultado) => {
      notificar('success', 'Documentos enviados. Lendo os arquivos…')
      navegar(`/tasks/auditoria/${resultado.id}`)
    },
    onError: (e: Error) => {
      setErro(e.message)
      notificar('error', e.message)
    },
  })

  const maximo = config?.maximo_arquivos ?? 10

  function adicionar(lista: FileList | null) {
    if (!lista?.length) return
    const novos = Array.from(lista)
    const total = arquivos.length + novos.length
    if (total > maximo) {
      setErro(`Máximo de ${maximo} arquivos por auditoria.`)
      return
    }
    setErro('')
    setArquivos([...arquivos, ...novos])
    setTipos([...tipos, ...novos.map(() => config?.tipo_composto ?? 'composto')])
  }

  function remover(indice: number) {
    setArquivos(arquivos.filter((_, i) => i !== indice))
    setTipos(tipos.filter((_, i) => i !== indice))
  }

  function definirTipo(indice: number, valor: string) {
    setTipos(tipos.map((t, i) => (i === indice ? valor : t)))
  }

  function enviar() {
    if (!arquivos.length) {
      setErro('Selecione ao menos um documento para auditar.')
      return
    }
    setErro('')
    criar.mutate()
  }

  if (isLoading) {
    return (
      <p className="p-8 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando…
      </p>
    )
  }

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 p-6 text-white">
          <h1 className="flex items-center text-2xl font-bold">
            <FontAwesomeIcon icon={faCloudUploadAlt} className="mr-3" />
            Nova Auditoria
          </h1>
          <p className="mt-1 text-rose-100">
            Envie os documentos do processo. A leitura classifica o conteúdo e
            devolve a lista para você confirmar o que será auditado.
          </p>
        </div>
      </header>

      {erro && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
          <span>{erro}</span>
        </div>
      )}

      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
          Fase do processo
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {(config?.fases ?? []).map((f) => (
            <label
              key={f.id}
              className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
                fase === f.id
                  ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20'
                  : 'border-slate-200 hover:border-rose-300 dark:border-slate-600'
              }`}
            >
              <input
                type="radio"
                name="fase"
                value={f.id}
                checked={fase === f.id}
                onChange={() => {
                  setFase(f.id)
                  // os tipos válidos mudam com a fase: zera a classificação
                  setTipos(arquivos.map(() => config?.tipo_composto ?? 'composto'))
                }}
                className="sr-only"
              />
              <p className="font-bold text-slate-800 dark:text-slate-100">
                {f.nome}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {f.descricao}
              </p>
              <p className="mt-2 text-xs font-medium text-rose-600 dark:text-rose-400">
                {f.verifica}
              </p>
            </label>
          ))}
        </div>
      </section>

      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
          Documentos
        </h2>

        <div className="rounded-2xl border-2 border-dashed border-rose-300 bg-gradient-to-br from-rose-50 to-orange-50 p-8 text-center dark:border-rose-700 dark:from-rose-900/20 dark:to-orange-900/20">
          <FontAwesomeIcon
            icon={faCloudUploadAlt}
            className="mb-3 text-3xl text-rose-500"
          />
          <div>
            <button
              type="button"
              onClick={() => entrada.current?.click()}
              className="text-lg font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400"
            >
              Escolher arquivos
            </button>
            <input
              ref={entrada}
              id="documentos"
              type="file"
              multiple
              accept={EXTENSOES}
              className="hidden"
              onChange={(e) => {
                adicionar(e.target.files)
                e.target.value = ''
              }}
            />
          </div>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Máximo de {maximo} arquivos • PDF, DOC, DOCX ou TXT
          </p>
        </div>

        {arquivos.length > 0 && (
          <ul className="mt-6 space-y-3">
            {arquivos.map((arquivo, indice) => (
              <li
                key={`${arquivo.name}-${indice}`}
                className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"
              >
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className="flex min-w-0 items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                    <FontAwesomeIcon icon={faFileAlt} className="text-rose-500" />
                    <span className="truncate">{arquivo.name}</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => remover(indice)}
                    aria-label={`Remover ${arquivo.name}`}
                    className="rounded-lg px-2 py-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400">
                  Tipo do documento
                  <select
                    aria-label={`Tipo de ${arquivo.name}`}
                    value={tipos[indice] ?? 'composto'}
                    onChange={(e) => definirTipo(indice, e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                  >
                    <option value={config?.tipo_composto ?? 'composto'}>
                      Documento composto (vários documentos no mesmo arquivo)
                    </option>
                    {tiposDaFase.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.rotulo}
                      </option>
                    ))}
                  </select>
                </label>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-6 dark:border-amber-700 dark:bg-amber-900/20">
        <h2 className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
          <FontAwesomeIcon icon={faLandmark} className="text-amber-600" />
          Decretos municipais
          <span className="font-normal normal-case text-amber-700 dark:text-amber-400">
            (opcional)
          </span>
        </h2>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
          Normas locais que complementam a Lei 14.133/2021 entram na análise
          junto com a lei federal.
        </p>

        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          Município
          <select
            value={municipio}
            onChange={(e) => {
              setMunicipio(e.target.value)
              setDecretos([])
            }}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
          >
            <option value="">Sem decreto municipal</option>
            {(config?.municipios ?? []).map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>

        {municipio && (
          <div className="mt-4">
            <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
              Decretos disponíveis
            </p>
            {(decretosDisponiveis?.decretos ?? []).length === 0 ? (
              <p className="text-sm text-slate-500">
                Nenhum decreto cadastrado para {municipio}.
              </p>
            ) : (
              <ul className="space-y-2">
                {(decretosDisponiveis?.decretos ?? []).map((d) => (
                  <li key={d.id}>
                    <label className="flex cursor-pointer items-start gap-2 rounded-lg bg-white p-3 text-sm dark:bg-slate-800">
                      <input
                        type="checkbox"
                        checked={decretos.includes(d.id)}
                        onChange={(e) =>
                          setDecretos(
                            e.target.checked
                              ? [...decretos, d.id]
                              : decretos.filter((x) => x !== d.id),
                          )
                        }
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium text-slate-800 dark:text-slate-100">
                          Decreto {d.numero}
                          {d.ano && `/${d.ano}`}
                        </span>
                        {d.titulo && (
                          <span className="block text-xs text-slate-500">
                            {d.titulo}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <label className="mt-4 block text-sm font-medium text-slate-700 dark:text-slate-300">
          Enviar decreto que ainda não está cadastrado
          <input
            type="file"
            multiple
            accept={EXTENSOES}
            onChange={(e) => setDecretosNovos(Array.from(e.target.files ?? []))}
            className="mt-1 block w-full text-sm text-slate-600 dark:text-slate-400"
          />
        </label>
      </section>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navegar('/auditoria')}
          className="rounded-xl border border-slate-300 px-6 py-3 font-medium text-slate-600 dark:border-slate-600 dark:text-slate-300"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={enviar}
          disabled={criar.isPending}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-rose-600 hover:to-amber-600 disabled:opacity-60"
        >
          <FontAwesomeIcon
            icon={criar.isPending ? faSpinner : faCloudUploadAlt}
            spin={criar.isPending}
          />
          {criar.isPending ? 'Enviando…' : 'Enviar e ler documentos'}
        </button>
      </div>
    </div>
  )
}
